import { defineChatPlace } from '../../../chat/panel';
import { boardChat } from '../boardChat';
import type { ReportPayload } from '../../../types/reportDashboard';

/**
 * The chat docked beside a report board. The same place as the cohort
 * board's — same features, same panel switch — with starters for the
 * questions this report raises rather than answers.
 */
export const reportChat = defineChatPlace({
  ...boardChat,
  welcome: { title: '', subtitle: 'Ask about the applications, the cases waiting, or the policies on the books.' },
  // What an operations lead asks after reading the page: which cases are
  // stuck, why a figure looks the way it does, what a filter would change.
  starters: [
    'Which cases have been waiting longest, and what are they waiting on?',
    'Why is the placement rate higher than it really is?',
    'How many applications sent in July are still pending?',
    'What reasons does the carrier give most often on declined cases?',
  ],
  placeholder: 'Ask about this report…',
});

/**
 * What the agent is told the reader is looking at: the range, the filters, the
 * headline figures and how big each case list is — not the cases themselves,
 * which `read_ops_report` fetches.
 *
 * Every term that stands in for something the warehouse does not hold is sent
 * with its note, so the agent can repeat it rather than quoting the figure
 * bare.
 */
export function buildReportSurfaceContext(
  dashboardId: string,
  payload: ReportPayload,
  filters: Record<string, string[]>,
): Record<string, unknown> {
  const marked = Object.values(payload.terms ?? {})
    .filter((t) => t.proxy || t.missing)
    .map((t) => ({ label: t.label, note: t.missing ?? t.proxy, source: t.source }));

  return {
    type: 'manual_dashboard',
    kind: 'report',
    dashboard_id: dashboardId,
    name: payload.spec.name,
    range: payload.range,
    grain: payload.grain,
    filters: Object.fromEntries(Object.entries(filters).filter(([, v]) => v.length)),
    as_of: payload.meta.as_of,
    headline: (payload.kpis ?? []).map((k) => ({ label: k.label, total: k.total, format: k.format })),
    case_lists: {
      'Pending (in UW)': payload.pending_uw?.total,
      'Pending (carrier)': payload.pending_carrier?.total,
      'Approved as is': payload.approved_as_is?.total,
      'Approved better': payload.approved_better?.total,
      'Approved worse': payload.approved_worse?.total,
      Declined: payload.declined?.total,
      'Policies Issued, not yet paid': payload.issued?.total,
      'Policies being paid': payload.paid?.total,
      'Policies lapsed': payload.inactive?.total,
    },
    marked,
  };
}
